import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import IntegrationsGridRepository from '../repository/IntegrationsGridRepository';
import IntegrationsGridComponent from '../component/IntegrationsGridComponent';
import IntegrationsGridView from '../view/IntegrationsGridView';
import integrationsGridTemplate from '../view/template';
import { IntegrationsGridViewData } from '../types';
import { GridEvents } from '../../../grid/types/common';
import { Integration, Sensor, SensorEnabledStatus, IntegrationEvents } from '../../common/types';
import { CreateIntegrationEvent } from '../../addIntegrationPanel/types/CreateIntegrationEvent';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { SensorDeleteActionErrors } from '../../sensors/constants';
import { SensorType } from '../../sensors/types/sensor-type';
import { ElementClass } from '../../../utils/htmlElement.utils';

export default class IntegrationsGridController extends BaseWebComponentController {
  public dataRepository: IntegrationsGridRepository;
  public view: IntegrationsGridView;

  constructor(component: IntegrationsGridComponent) {
    super(component);

    this.initialSetup({
      dataRepositoryConstructor: IntegrationsGridRepository,
      viewConstructor: IntegrationsGridView,
      viewTemplate: integrationsGridTemplate,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    this.view.dom.$grid.onDataLoadingFinishedWithSuccess = () => this.view.handleGridVisibility();
    this.view.dom.$grid.onDataLoadingFinishedWithError = () => this.view.showGrid();

    return [
      {
        element: this.view.dom.$noResultsAddNewButton,
        event: 'click',
        callback: this.handleAddWindow,
      },
      {
        element: this.view.dom.$addIntegration,
        event: CreateIntegrationEvent.OnModifyDialogTitle,
        callback: (evt: WebComponentEvent) => {
          if (evt.detail.title) {
            this.view.modifyDialogTitle(evt.detail.title);
          }
        },
      },
      {
        element: this.view.dom.$addNewButton,
        event: 'click',
        callback: this.handleAddWindow,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROW_CLICKED,
        callback: (evt: WebComponentEvent) => {
          if (evt.detail.rowDetails) {
            this.view.openDetailsPanel(evt.detail.rowDetails as Integration);
          }
        },
      },
      {
        element: this.view.dom.$addIntegration,
        event: CreateIntegrationEvent.OnSwitchToSelectType,
        callback: this.refreshData,
      },
      {
        element: document.body,
        event: IntegrationEvents.SENSOR_SET_ENABLED_STATUS,
        callback: (evt: WebComponentEvent) =>
          this.sensorSetEnabledStatus(evt.detail.sensor as Sensor, evt.detail.enabledStatus as SensorEnabledStatus),
      },
      {
        element: document.body,
        event: IntegrationEvents.INTEGRATION_SET_ENABLED_STATUS,
        callback: (evt: WebComponentEvent) =>
          this.setEnabledStatus(evt.detail.integration as Integration, evt.detail.enabledStatus as SensorEnabledStatus),
      },
      {
        element: document.body,
        event: IntegrationEvents.INTEGRATION_DELETE,
        callback: (evt: WebComponentEvent) => this.dataRepository.remove(evt.detail as Integration),
      },
      {
        element: document.body,
        event: CreateIntegrationEvent.OnWindowCancel,
        callback: this.onCloseIntegration,
      },
      {
        element: document.body,
        event: IntegrationEvents.INTEGRATION_EDIT,
        callback: (evt: WebComponentEvent) => this.view.openEditWindow(evt.detail as Integration),
      },
      {
        element: document.body,
        event: IntegrationEvents.DELETE_SENSOR,
        callback: this.deleteSensor,
      },
    ];
  }

  /**
   *
   * @param sensor
   * @param enabledStatus
   */
  async sensorSetEnabledStatus(sensor: Sensor, enabledStatus: SensorEnabledStatus) {
    try {
      const response = await this.dataRepository.sensorSetEnabledStatus(sensor, enabledStatus);
      if (response['success']) {
        const message =
            SensorEnabledStatus.DISABLED === enabledStatus
              ? this.dependencies.translateFn('Your sensor has been disabled')
              : this.dependencies.translateFn('Sensor has been enabled'),
          severity = SensorEnabledStatus.DISABLED === enabledStatus ? ToastSeverity.INFORMATIVE : ToastSeverity.SUCCESS;

        this.handleSuccessResponse(message, severity);
        if (response['data'] && response['postEnablingMessage']) {
          this.view.showSuccessToast(response['postEnablingMessage'], ToastSeverity.INFORMATIVE);
        }

        return;
      }
      this.view.showResponseError(response['data']);
    } catch (e) {
      this.handleException(e);
    }
  }

  /**
   *
   * @param integration
   * @param enabledStatus
   */
  async setEnabledStatus(integration: Integration, enabledStatus: SensorEnabledStatus) {
    try {
      const response = await this.dataRepository.setEnabledStatus(integration, enabledStatus);
      if (response['success']) {
        const message =
            SensorEnabledStatus.DISABLED === enabledStatus
              ? this.dependencies.translateFn('The sensor integration will be disabled shortly.')
              : this.dependencies.translateFn('The sensor integration will be enabled shortly.'),
          severity = SensorEnabledStatus.DISABLED === enabledStatus ? ToastSeverity.INFORMATIVE : ToastSeverity.SUCCESS;
        this.handleSuccessResponse(message, severity);
        return;
      }
      this.view.showResponseError(response['data']);
    } catch (e) {
      this.handleException(e);
    }
  }

  /**
   *
   * @param message
   * @param severity
   * @private
   */
  private handleSuccessResponse(message: string, severity: ToastSeverity) {
    this.view.showSuccessToast(message, severity);
    this.refreshGrid();
  }

  /**
   *
   * @private
   * @param exception
   */
  private handleException(exception: Error) {
    const errorMessage = exception?.message || this.translator.getGenericErrorMessage();
    this.view.showErrorToast(errorMessage);
  }

  private async refreshData() {
    await this.view.dom.$addIntegration.controller.render();
  }

  async getDataForView(): Promise<IntegrationsGridViewData> {
    return this.dataRepository.getDataForView();
  }

  refreshGrid(): void {
    if (!this.view.dom.$grid.controller) {
      return;
    }
    this.view.dom.$grid.controller.render();
  }

  public async refreshDataAfterLicenseChange() {
    await this.refreshData();
    this.component.dispatchEvent(
      new CustomEvent(CreateIntegrationEvent.OnLicenseChanged, {
        bubbles: true,
        composed: true,
      })
    );
  }

  private async deleteSensor(evt: WebComponentEvent) {
    const deletedSensor = evt.detail as Sensor;

    try {
      await this.dataRepository.deleteSensor(deletedSensor);
      this.view.showSuccessToast('Domain controller sensor has been deleted', ToastSeverity.SUCCESS);
      this.refreshGrid();
    } catch (e) {
      let message = this.translator.getGenericErrorMessage();

      switch (e.code) {
        case SensorDeleteActionErrors.USER_DOES_NOT_EXIST_OR_DO_NOT_HAVE_PERMISSION:
          message = this.dependencies.translateFn('User does not have access to this company.');
          break;
        case SensorDeleteActionErrors.CANNOT_DELETE_LAST_SENSOR_IN_THE_INTEGRATION:
          if (deletedSensor.type == SensorType.AD_ONPREM_DOMAIN) {
            message = this.dependencies.translateFn(
              'Cannot delete the last domain controller sensor in the integration.'
            );
          }
          break;
        case SensorDeleteActionErrors.CANNOT_DELETE_SENSOR_THAT_IS_NOT_OFFLINE_OR_UNMANAGED:
          if (deletedSensor.type == SensorType.AD_ONPREM_DOMAIN) {
            message = this.dependencies.translateFn('The sensor must be offline or unmanaged.');
          }
          break;
        default:
          break;
      }

      this.view.showErrorToast(message);
      this.refreshGrid();
    }
  }

  onCloseIntegration(): void {
    if (!this.view.dialog) {
      return;
    }
    this.view.dialog.dismiss();
    this.view.resetAddIntegrationWindow();
    this.refreshGrid();
  }

  handleAddWindow(): void {
    if (!this.view.dom.$addIntegration.classList.contains(ElementClass.HIDDEN)) {
      this.view.resetAddIntegrationWindow();
      this.refreshGrid();
    }
    this.view.openAddWindow();
  }
}
